/**
 * Utility functions for intelligent avatar selection
 */

import type { AvatarData } from '@/app/components/AvatarPicker/schemas';
import { Gender } from '@/api-gen/api.schemas';

/**
 * Selects a default avatar intelligently based on available avatars and gender
 * @param avatars - Array of available avatars
 * @param gender - Gender to filter by
 * @param excludeRecent - Optional array of recently used avatar URLs to avoid
 * @returns Selected avatar URL or null if no suitable avatar found
 */
export function selectDefaultAvatar(
  avatars: AvatarData[], 
  gender: Gender,
  excludeRecent: string[] = []
): string | null {
  if (!avatars || avatars.length === 0) {
    return null;
  }

  // Convert Gender enum to string for matching
  const genderString = gender === Gender.Male ? 'male' : 
                      gender === Gender.Female ? 'female' : '';

  // Filter avatars by gender if gender string is available
  let candidateAvatars = genderString 
    ? avatars.filter(avatar => 
        avatar.gender?.toLowerCase() === genderString.toLowerCase()
      )
    : avatars;

  // If no gender-specific avatars found, fall back to all avatars
  if (candidateAvatars.length === 0) {
    candidateAvatars = [...avatars];
  }

  // Remove recently used avatars from candidates
  if (excludeRecent.length > 0) {
    const filteredCandidates = candidateAvatars.filter(
      avatar => !excludeRecent.includes(avatar.url)
    );
    
    // Only use filtered list if we still have options
    if (filteredCandidates.length > 0) {
      candidateAvatars = filteredCandidates;
    }
  }

  // Prefer "Flat" collection as it's the default in AvatarPicker
  const flatCollectionAvatars = candidateAvatars.filter(
    avatar => avatar.collection?.toLowerCase() === 'flat'
  );

  const finalCandidates = flatCollectionAvatars.length > 0 
    ? flatCollectionAvatars 
    : candidateAvatars;

  // If we have multiple options, pick randomly for variety
  if (finalCandidates.length > 1) {
    const randomIndex = Math.floor(Math.random() * finalCandidates.length);
    return finalCandidates[randomIndex].url;
  }

  // Return the single option or first available
  return finalCandidates.length > 0 ? finalCandidates[0].url : null;
}

/**
 * Gets a varied avatar selection by avoiding recently used ones
 * @param avatars - Array of available avatars
 * @param gender - Gender to filter by
 * @param recentlyUsed - Array of recently used avatar URLs
 * @param maxRecentCount - Maximum number of recent avatars to track
 * @returns Object with selected avatar URL and updated recent list
 */
export function getVariedAvatarSelection(
  avatars: AvatarData[],
  gender: Gender,
  recentlyUsed: string[] = [],
  maxRecentCount: number = 3
): { avatarUrl: string | null; updatedRecent: string[] } {
  const selectedUrl = selectDefaultAvatar(avatars, gender, recentlyUsed);
  
  if (!selectedUrl) {
    return { avatarUrl: null, updatedRecent: recentlyUsed };
  }

  // Update recently used list
  const updatedRecent = [selectedUrl, ...recentlyUsed]
    .filter((url, index, arr) => arr.indexOf(url) === index) // Remove duplicates
    .slice(0, maxRecentCount); // Keep only the most recent

  return { avatarUrl: selectedUrl, updatedRecent };
}

/**
 * Fallback avatar URLs for cases where API doesn't return suitable avatars
 * These are the same URLs that were previously hardcoded
 */
export const FALLBACK_AVATARS: Record<Gender, string> = {
  [Gender.Female]: 'https://scenarioeditorstdev.blob.core.windows.net/avatars/Flat/female/avatar_flat_female.webp',
  [Gender.Male]: 'https://scenarioeditorstdev.blob.core.windows.net/avatars/Flat/male/0_0_640_N-4.webp'
};

/**
 * Gets a default avatar with fallback to hardcoded URLs if no avatars available
 * @param avatars - Array of available avatars
 * @param gender - Gender to filter by
 * @param excludeRecent - Optional array of recently used avatar URLs to avoid
 * @returns Avatar URL (never null, always returns something)
 */
export function selectDefaultAvatarWithFallback(
  avatars: AvatarData[],
  gender: Gender,
  excludeRecent: string[] = []
): string {
  const selected = selectDefaultAvatar(avatars, gender, excludeRecent);
  
  if (selected) {
    return selected;
  }

  // Fallback to hardcoded defaults
  return FALLBACK_AVATARS[gender] || FALLBACK_AVATARS[Gender.Female];
}